/**
 * ShareArtworkDialog — customer-facing modal to manage external designer
 * collaboration links.
 *
 * Customer can:
 *   • generate a new tokenised share URL (optional password, 30-day default
 *     expiry, optional auto-expire on artwork approval)
 *   • copy or revoke existing links
 *
 * The designer-facing public page lives at `/artwork-share/:token`.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Copy, Trash2, Loader2, Plus, ExternalLink, Lock, Mail, Eye, MessageSquare, RefreshCw } from 'lucide-react';
import { ordersApi } from '../lib/api';
import axios from 'axios';
import { API_BASE } from '../lib/apiBase';

const API = `${API_BASE}/api`;

export function ShareArtworkDialog({ orderId, open, onOpenChange }) {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [password, setPassword] = useState('');
    const [expireOnApproval, setExpireOnApproval] = useState(false);
    const [expiresInDays, setExpiresInDays] = useState(30);
    const [designerName, setDesignerName] = useState('');
    const [designerEmail, setDesignerEmail] = useState('');
    const [personalMessage, setPersonalMessage] = useState('');

    const fetchLinks = useCallback(async () => {
        if (!orderId) return;
        setLoading(true);
        try {
            const res = await ordersApi.listArtworkShareLinks(orderId);
            setLinks(res.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load share links');
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        if (open) fetchLinks();
    }, [open, fetchLinks]);

    const handleCreate = async () => {
        setCreating(true);
        try {
            const res = await ordersApi.createArtworkShareLink(orderId, {
                password: password.trim() || null,
                expires_in_days: expiresInDays,
                expire_on_approval: expireOnApproval,
                designer_name: designerName.trim() || null,
                designer_email: designerEmail.trim() || null,
                personal_message: personalMessage.trim() || null,
            });
            setLinks((prev) => [res.data, ...prev]);
            setPassword('');
            setExpireOnApproval(false);
            setExpiresInDays(30);
            setDesignerName('');
            setDesignerEmail('');
            setPersonalMessage('');
            toast.success(
                res.data.invitation_sent_at
                    ? `Invitation sent to ${res.data.designer_email}`
                    : 'Share link created — copy and send manually'
            );
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create share link');
        } finally {
            setCreating(false);
        }
    };

    const handleResend = async (link) => {
        try {
            await axios.post(`${API}/orders/${orderId}/artwork-share-links/${link.id}/resend`);
            setLinks((prev) =>
                prev.map((l) => (l.id === link.id ? { ...l, invitation_sent_at: new Date().toISOString() } : l))
            );
            toast.success(`Invitation resent to ${link.designer_email}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to resend invitation');
        }
    };

    const handleRevoke = async (linkId) => {
        if (!window.confirm('Revoke this link? Anyone with the URL will lose access immediately.')) return;
        try {
            await ordersApi.revokeArtworkShareLink(orderId, linkId);
            setLinks((prev) =>
                prev.map((l) =>
                    l.id === linkId ? { ...l, revoked_at: new Date().toISOString() } : l
                )
            );
            toast.success('Link revoked');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to revoke link');
        }
    };

    const handleCopy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copied to clipboard');
        } catch {
            toast.error('Could not copy — select the URL manually');
        }
    };

    const formatExpiry = (iso) => {
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric',
            });
        } catch { return iso; }
    };

    const activeLinks = links.filter((l) => !l.revoked_at);
    const revokedLinks = links.filter((l) => l.revoked_at);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="share-artwork-dialog">
                <DialogHeader>
                    <DialogTitle>Share with external designer</DialogTitle>
                    <DialogDescription>
                        Generate a read-only link your designer can use to review files and leave comments — no Owntune account required.
                    </DialogDescription>
                </DialogHeader>

                {/* Create new link */}
                <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-slate-50/40">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="designer-name" className="text-xs text-slate-600">
                                Designer name (optional)
                            </Label>
                            <Input
                                id="designer-name"
                                value={designerName}
                                onChange={(e) => setDesignerName(e.target.value)}
                                placeholder="e.g. Jane Smith"
                                className="h-9"
                                data-testid="designer-name-field"
                            />
                        </div>
                        <div>
                            <Label htmlFor="designer-email" className="text-xs text-slate-600">
                                Designer email (sends invitation)
                            </Label>
                            <Input
                                id="designer-email"
                                type="email"
                                value={designerEmail}
                                onChange={(e) => setDesignerEmail(e.target.value)}
                                placeholder="designer@agency.com"
                                className="h-9"
                                data-testid="designer-email-field"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="personal-message" className="text-xs text-slate-600">
                            Personal message (optional)
                        </Label>
                        <textarea
                            id="personal-message"
                            value={personalMessage}
                            onChange={(e) => setPersonalMessage(e.target.value)}
                            placeholder="Hi Jane — please review the front cover, the back has been approved already…"
                            rows={2}
                            className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            data-testid="personal-message-field"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="share-password" className="text-xs text-slate-600">
                                Password (optional)
                            </Label>
                            <Input
                                id="share-password"
                                type="text"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Leave empty for no password"
                                className="h-9"
                                data-testid="share-password-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="share-expiry" className="text-xs text-slate-600">
                                Expires in (days)
                            </Label>
                            <Input
                                id="share-expiry"
                                type="number"
                                min="1"
                                max="365"
                                value={expiresInDays}
                                onChange={(e) => setExpiresInDays(parseInt(e.target.value || '30', 10))}
                                className="h-9"
                                data-testid="share-expiry-input"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="text-sm">
                            <p className="font-medium text-slate-700">Auto-expire on approval</p>
                            <p className="text-xs text-slate-500">Link stops working once you approve the artwork.</p>
                        </div>
                        <Switch
                            checked={expireOnApproval}
                            onCheckedChange={setExpireOnApproval}
                            data-testid="share-expire-on-approval"
                        />
                    </div>
                    <Button
                        onClick={handleCreate}
                        disabled={creating}
                        className="w-full"
                        data-testid="create-share-link-btn"
                    >
                        {creating ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                        ) : designerEmail.trim() ? (
                            <><Mail className="h-4 w-4 mr-2" />Send invitation</>
                        ) : (
                            <><Plus className="h-4 w-4 mr-2" />Generate link only</>
                        )}
                    </Button>
                </div>

                {/* Existing links */}
                {loading ? (
                    <div className="text-center py-6 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
                        Loading existing links...
                    </div>
                ) : activeLinks.length === 0 && revokedLinks.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-2">
                        No share links yet. Generate one above to invite your designer.
                    </p>
                ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {activeLinks.map((link) => {
                            const designerLabel = link.designer_name || link.designer_email || 'Unnamed designer';
                            const statusBadges = [];
                            if (link.last_opened_at) {
                                statusBadges.push(
                                    <span key="opened" className="inline-flex items-center gap-1 text-emerald-600">
                                        <Eye className="h-3 w-3" />
                                        Opened {new Date(link.last_opened_at).toLocaleDateString()}
                                    </span>
                                );
                            } else if (link.designer_email) {
                                statusBadges.push(
                                    <span key="pending" className="text-slate-400">Never opened</span>
                                );
                            }
                            if (link.comment_count > 0) {
                                statusBadges.push(
                                    <span key="comments" className="inline-flex items-center gap-1 text-purple-600">
                                        <MessageSquare className="h-3 w-3" />
                                        {link.comment_count} comment{link.comment_count === 1 ? '' : 's'}
                                    </span>
                                );
                            }
                            return (
                                <div
                                    key={link.id}
                                    className="flex items-start justify-between gap-2 p-3 border border-slate-200 rounded-lg bg-white"
                                    data-testid={`share-link-${link.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="text-sm font-semibold text-slate-800 truncate">
                                                {designerLabel}
                                            </span>
                                            {link.has_password && (
                                                <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                                    <Lock className="h-3 w-3" /> password
                                                </span>
                                            )}
                                            {link.expire_on_approval && (
                                                <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                                    auto-expires on approval
                                                </span>
                                            )}
                                        </div>
                                        {link.designer_email && (
                                            <p className="text-xs text-slate-500 truncate">{link.designer_email}</p>
                                        )}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px]">
                                            <span className="text-slate-400">
                                                Expires {formatExpiry(link.expires_at)}
                                            </span>
                                            {statusBadges}
                                        </div>
                                        <p className="text-[10px] font-mono text-slate-400 mt-1 truncate" title={link.url}>
                                            {link.url}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-0.5 flex-shrink-0">
                                        {link.designer_email && (
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8"
                                                onClick={() => handleResend(link)}
                                                data-testid={`resend-invite-${link.id}`}
                                                title="Resend invitation email"
                                            >
                                                <RefreshCw className="h-4 w-4" />
                                            </Button>
                                        )}
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-8 w-8"
                                            onClick={() => handleCopy(link.url)}
                                            data-testid={`copy-share-link-${link.id}`}
                                            title="Copy link"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-8 w-8"
                                            onClick={() => window.open(link.url, '_blank')}
                                            title="Open"
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-8 w-8 text-red-500 hover:bg-red-50"
                                            onClick={() => handleRevoke(link.id)}
                                            data-testid={`revoke-share-link-${link.id}`}
                                            title="Revoke"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                        {revokedLinks.length > 0 && (
                            <details className="text-xs text-slate-400">
                                <summary className="cursor-pointer py-1">
                                    {revokedLinks.length} revoked link{revokedLinks.length === 1 ? '' : 's'}
                                </summary>
                                {revokedLinks.map((l) => (
                                    <div key={l.id} className="px-3 py-1 line-through opacity-60">
                                        {l.url}
                                    </div>
                                ))}
                            </details>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ShareArtworkDialog;
